import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Marker, Circle, Polygon } from '@react-google-maps/api';
import GoogleMapProvider from '../../components/map/GoogleMapProvider';
import BaseMap, { MapPosition } from '../../components/map/BaseMap';
import { useProject } from '../../contexts/ProjectContext';
import { useAuth } from '../../contexts/AuthContext';
import { seekFindService } from '../../services/seekFind.service';
import { mapToolsService } from '../../services/mapTools.service';
import { surveyService } from '../../services/survey.service';
import { surveyResponseService } from '../../services/surveyResponse.service';
import SurveyPreview from '../../components/surveys/preview/SurveyPreview';
import { Survey } from '../../types/survey.types';
import { MapArea, MapPin, pinCategoryMeta } from '../../types/maps.types';
import { DiscoveredLocation, SeekOutcome, SEEK_OUTCOMES, seekOutcomeMeta } from '../../types/seekFind.types';
import { Crosshair, Loader2, X, Layers, BarChart3 } from 'lucide-react';
import { toast } from 'react-hot-toast';

// Area name label biased into the polygon's top-left.
const labelPos = (paths: { lat: number; lng: number }[]) => {
  const lats = paths.map(p => p.lat), lngs = paths.map(p => p.lng);
  const north = Math.max(...lats), south = Math.min(...lats), west = Math.min(...lngs), east = Math.max(...lngs);
  return { lat: north - (north - south) * 0.06, lng: west + (east - west) * 0.10 };
};
const areaLabel = (name: string) => ({
  clickable: false,
  label: { text: name, color: 'rgba(31,41,55,0.8)', fontSize: '13px', fontWeight: '700' } as any,
  icon: { path: 0 /* CIRCLE */, scale: 0, fillOpacity: 0, strokeOpacity: 0 } as any,
});

const SeekFindInner: React.FC = () => {
  const { selectedProjectId, selectedProject } = useProject();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  const [gps, setGps] = useState<{ lat: number; lng: number; accuracy?: number } | null>(null);
  const [candidate, setCandidate] = useState<MapPosition | null>(null); // tapped override
  const [locations, setLocations] = useState<DiscoveredLocation[]>([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const watchId = useRef<number | null>(null);

  // Saved map overlays for this project (areas + typed pins from the Maps tool).
  const [areas, setAreas] = useState<MapArea[]>([]);
  const [pins, setPins] = useState<MapPin[]>([]);
  const [showLayers, setShowLayers] = useState(false);
  const [visibleAreaIds, setVisibleAreaIds] = useState<Set<string>>(new Set());
  const [showPins, setShowPins] = useState(false);
  const [campuses, setCampuses] = useState<{ name: string; lat: number; lng: number }[]>([]);
  const [visibleCampusIds, setVisibleCampusIds] = useState<Set<string>>(new Set());
  const [showStats, setShowStats] = useState(false);
  const [visibleOutcomes, setVisibleOutcomes] = useState<Set<SeekOutcome>>(new Set(['no_answer', 'attempt', 'survey', 'success']));

  // Door survey (4th disposition): logs the spot like an attempt, then opens
  // the project's active survey so the conversation gets documented even when
  // it isn't a signature/lead. Response links back to the discovered location.
  const [surveyModal, setSurveyModal] = useState<{ survey: Survey; locationId: string; point: MapPosition } | null>(null);
  // Most recent logged spot this session — enables the field "Undo last" button.
  const [lastLogged, setLastLogged] = useState<{ id: string; outcome: SeekOutcome } | null>(null);

  // Map center is set ONCE for initial positioning, then left alone so the map
  // stays where the user pans/scrolls. Live GPS must NOT feed the map's `center`
  // prop (BaseMap passes it straight to <GoogleMap>, a controlled prop) or every
  // watchPosition tick snaps the view back to the user. GPS still drives the
  // "you" marker + the recenter button below. Prefer the project's school
  // location; otherwise lock to the first GPS fix.
  const [initialCenter, setInitialCenter] = useState<MapPosition | null>(null);
  const [initialZoom, setInitialZoom] = useState(17);
  useEffect(() => {
    if (initialCenter) return; // lock once set — never override the user's pan
    const loc = selectedProject?.schoolAddress?.location || gps;
    if (loc) { setInitialCenter({ lat: loc.lat, lng: loc.lng }); return; }
    // No school location and no GPS fix (denied/pending): open over the
    // project's saved areas (e.g. a district boundary) at a zoom fitted to
    // their extent, instead of the continental-US default view.
    if (areas.length > 0) {
      let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
      areas.forEach(a => (a.paths || []).forEach(p => {
        if (p.lat < minLat) minLat = p.lat; if (p.lat > maxLat) maxLat = p.lat;
        if (p.lng < minLng) minLng = p.lng; if (p.lng > maxLng) maxLng = p.lng;
      }));
      if (maxLat >= minLat && maxLng >= minLng) {
        const span = Math.max(maxLat - minLat, maxLng - minLng);
        setInitialZoom(span > 1 ? 8 : span > 0.5 ? 9 : span > 0.25 ? 10 : span > 0.12 ? 11 : span > 0.06 ? 12 : span > 0.03 ? 13 : 14);
        setInitialCenter({ lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 });
      }
    }
  }, [selectedProject, gps, areas, initialCenter]);
  const mapCenter = initialCenter || { lat: 39.8283, lng: -98.5795 };

  // Live GPS
  useEffect(() => {
    if (!navigator.geolocation) return;
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => {},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
    return () => { if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current); };
  }, []);

  // Load the project's saved areas + pins (to optionally overlay while working).
  useEffect(() => {
    let active = true;
    if (!selectedProjectId) { setAreas([]); setPins([]); return; }
    Promise.all([mapToolsService.getAreas(selectedProjectId), mapToolsService.getPins(selectedProjectId)])
      .then(([a, p]) => { if (active) { setAreas(a); setPins(p); } })
      .catch(() => {});
    return () => { active = false; };
  }, [selectedProjectId]);

  // Geocode each campus (segment) so field staff can overlay them too.
  useEffect(() => {
    if (!(window as any).google) return;
    let active = true;
    const segs = selectedProject?.schoolSegments || [];
    const items = segs.length > 0
      ? segs.map(s => ({ name: s.name, sa: s.schoolAddress }))
      : (selectedProject?.schoolAddress ? [{ name: selectedProject?.name || 'Campus', sa: selectedProject.schoolAddress }] : []);
    const addrOf = (sa: any) => sa ? [sa.address, sa.city, sa.state, sa.zipCode].filter(Boolean).join(', ') : '';
    const geocoder = new google.maps.Geocoder();
    (async () => {
      const out: { name: string; lat: number; lng: number }[] = [];
      for (const it of items) {
        const loc = (it.sa as any)?.location;
        if (loc?.lat) { out.push({ name: it.name, lat: loc.lat, lng: loc.lng }); continue; }
        const addr = addrOf(it.sa);
        if (!addr) continue;
        try { const r = await geocoder.geocode({ address: addr }); const g = r.results?.[0]?.geometry?.location; if (g) out.push({ name: it.name, lat: g.lat(), lng: g.lng() }); } catch { /* skip */ }
      }
      if (active) setCampuses(out);
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  const loadLocations = useCallback(async () => {
    if (!selectedProjectId) { setLocations([]); return; }
    setLocations(await seekFindService.getByProject(selectedProjectId));
  }, [selectedProjectId]);

  useEffect(() => { loadLocations(); }, [loadLocations]);

  // The point we'll record: tapped candidate, else current GPS.
  const activePoint = candidate || (gps ? { lat: gps.lat, lng: gps.lng } : null);

  const record = async (outcome: SeekOutcome) => {
    if (!selectedProjectId || !currentUser) { toast.error('Select a project first'); return; }
    if (!activePoint) { toast.error('Waiting for GPS — or tap the map to set a spot'); return; }

    if (outcome === 'success') {
      // The success pin is NOT written here — it's created when the capture
      // form is actually SUBMITTED (sf=1 params below), so an abandoned form
      // leaves no orphan green pins and every success on the map is a real
      // captured lead. Determination starts 'pending' — validity is set by the
      // capture flow's own rules (signature projects: valid on signature).
      const params = new URLSearchParams({
        projectId: selectedProjectId,
        lat: String(activePoint.lat),
        lng: String(activePoint.lng),
        status: 'warm',
        determination: 'pending',
        sf: '1',
        ...(!candidate && gps?.accuracy ? { sfAcc: String(Math.round(gps.accuracy)) } : {}),
      });
      navigate(`/leads/new?${params.toString()}`);
      return;
    }

    setSaving(true);
    try {
      const id = await seekFindService.create({
        projectId: selectedProjectId,
        location: { lat: activePoint.lat, lng: activePoint.lng },
        outcome,
        notes: notes.trim() || undefined as any,
        accuracy: candidate ? undefined as any : gps?.accuracy,
        capturedBy: currentUser.uid,
        capturedByName: userProfile?.name || userProfile?.email || 'Field User',
      } as any);
      setLastLogged({ id, outcome });

      if (outcome === 'survey') {
        // Spot is logged; now open the project's active survey to document the
        // conversation. No active survey → the pin still stands, just no form.
        const surveys = await surveyService.getProjectSurveys(selectedProjectId);
        const active = surveys.find(s => s.status === 'active');
        if (!active) {
          toast.error('No active survey on this project — spot logged without survey.');
        } else {
          setSurveyModal({ survey: active, locationId: id, point: { lat: activePoint.lat, lng: activePoint.lng } });
        }
      } else {
        toast.success(`Logged: ${seekOutcomeMeta(outcome).label}`);
      }
      setNotes('');
      setCandidate(null);
      await loadLocations();
    } catch (e) {
      console.error('Failed to record location:', e);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Undo the most recent logged spot (mis-tap / changed mind at the door).
  const undoLast = async () => {
    if (!lastLogged) return;
    try {
      await seekFindService.remove(lastLogged.id);
      toast.success(`Removed: ${seekOutcomeMeta(lastLogged.outcome).label}`);
      setLastLogged(null);
      await loadLocations();
    } catch (e) {
      console.error('Failed to undo:', e);
      toast.error('Failed to undo');
    }
  };

  // Save the door-survey responses and link them onto the logged spot.
  const saveDoorSurvey = async (responses: Record<string, any>) => {
    if (!surveyModal || !selectedProjectId || !currentUser) return;
    const resp = await surveyResponseService.create(
      {
        surveyId: surveyModal.survey.id,
        surveyVersion: surveyModal.survey.version,
        workloadId: '', // not workload-based — linkage is via the discovered location
        workloadName: 'Seek & Find',
        addressId: surveyModal.locationId,
        projectId: selectedProjectId,
        responses,
        startedAt: new Date(),
        addressStreet: 'Seek & Find pin',
      } as any,
      currentUser.uid,
      userProfile?.name || userProfile?.email || 'Field User',
      gps ? ({ lat: gps.lat, lng: gps.lng, accuracy: gps.accuracy || 10, timestamp: Date.now(), source: 'gps' } as any) : undefined
    );
    await seekFindService.attachSurveyResponse(surveyModal.locationId, resp.id);
  };

  const submitDoorSurvey = async (responses: Record<string, any>) => {
    try {
      await saveDoorSurvey(responses);
      toast.success('Survey recorded');
    } catch (e) {
      console.error('Failed to save survey response:', e);
      toast.error('Failed to save survey');
    } finally {
      setSurveyModal(null);
    }
  };

  // The survey's "add your name today?" rescue question answered YES: save what
  // we have, then route into the signature/lead capture form (same prefill as
  // a Success outcome, using the surveyed spot's coordinates).
  const surveyLeadCapture = async (partialResponses: Record<string, any>) => {
    if (!surveyModal || !selectedProjectId) return;
    const point = surveyModal.point;
    try {
      await saveDoorSurvey(partialResponses);
    } catch (e) {
      console.error('Failed to save survey before lead capture:', e);
    }
    setSurveyModal(null);
    const params = new URLSearchParams({
      projectId: selectedProjectId,
      lat: String(point.lat),
      lng: String(point.lng),
      status: 'warm',
      determination: 'pending',
      // Existing survey pin: on submitted capture it flips to success + links
      // the lead (instead of creating a second pin at the same door).
      sfLoc: surveyModal.locationId,
    });
    navigate(`/leads/new?${params.toString()}`);
  };

  const dotIcon = (color: string, scale = 6) => ((window as any).google ? {
    path: google.maps.SymbolPath.CIRCLE, fillColor: color, fillOpacity: 0.9,
    strokeColor: '#ffffff', strokeWeight: 1.5, scale,
  } : undefined);

  // Fly to an area (and make sure it's shown) when its name is tapped.
  const focusArea = (a: MapArea) => {
    if (!mapRef || !a.paths.length || !(window as any).google) return;
    const b = new google.maps.LatLngBounds();
    a.paths.forEach(p => b.extend(p));
    mapRef.fitBounds(b);
    setVisibleAreaIds(prev => new Set(prev).add(a.id));
    setShowLayers(false);
  };

  if (!selectedProjectId) {
    return <div className="p-6 text-gray-500">Select a project to use seek &amp; find.</div>;
  }

  const counts = SEEK_OUTCOMES.map(o => ({ ...o, n: locations.filter(l => l.outcome === o.value).length }));

  return (
    <div className="relative h-[calc(100vh-4rem)]">
      <BaseMap center={mapCenter} zoom={initialCenter ? initialZoom : 5} onClick={(p) => setCandidate(p)} onMapLoad={setMapRef}>
        {/* Accumulated universe of worked locations (toggle by outcome) */}
        {locations.filter(l => visibleOutcomes.has(l.outcome)).map(l => (
          <Marker key={l.id} position={l.location} icon={dotIcon(seekOutcomeMeta(l.outcome).color)} title={`${seekOutcomeMeta(l.outcome).label}${l.notes ? ' — ' + l.notes : ''}`} />
        ))}

        {/* Current GPS */}
        {gps && (
          <>
            <Circle center={{ lat: gps.lat, lng: gps.lng }} radius={gps.accuracy || 20} options={{ fillColor: '#3B82F6', fillOpacity: 0.12, strokeColor: '#3B82F6', strokeOpacity: 0.4, strokeWeight: 1, clickable: false }} />
            <Marker position={{ lat: gps.lat, lng: gps.lng }} icon={dotIcon('#2563EB', 5)} title="You" />
          </>
        )}

        {/* The candidate point (tapped override) */}
        {candidate && <Marker position={candidate} icon={dotIcon('#111827', 8)} title="Selected spot" />}

        {/* Saved map overlays (areas + pins toggled from the Layers control) */}
        {areas.filter(a => visibleAreaIds.has(a.id)).map(a => (
          <React.Fragment key={a.id}>
            <Polygon paths={a.paths} options={{ fillColor: a.color || '#4F46E5', fillOpacity: 0.12, strokeColor: a.color || '#4F46E5', strokeWeight: 2, clickable: false }} />
            <Marker position={labelPos(a.paths)} {...areaLabel(a.name)} />
          </React.Fragment>
        ))}
        {showPins && pins.map(p => (
          <Marker
            key={p.id}
            position={p.location}
            label={{ text: pinCategoryMeta(p.category).emoji, fontSize: '20px' } as any}
            icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 0, fillOpacity: 0, strokeOpacity: 0 } as any}
            title={p.name || pinCategoryMeta(p.category).label}
          />
        ))}
        {campuses.filter(c => visibleCampusIds.has(c.name)).map((c, i) => (
          <Marker
            key={`campus-${c.name}-${i}`}
            position={{ lat: c.lat, lng: c.lng }}
            label={{ text: '🏫', fontSize: '22px' } as any}
            icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 0, fillOpacity: 0, strokeOpacity: 0 } as any}
            title={`Campus: ${c.name}`}
          />
        ))}
      </BaseMap>

      {/* Recenter */}
      <button
        onClick={() => { if (gps && mapRef) { mapRef.panTo({ lat: gps.lat, lng: gps.lng }); mapRef.setZoom(18); } }}
        className="absolute top-3 right-3 z-10 bg-white rounded-full shadow p-2 text-gray-700 hover:bg-gray-50"
        title="Center on me"
      ><Crosshair className="w-5 h-5" /></button>

      {/* Layers — overlay the project's saved maps/pins */}
      <div className="absolute top-16 right-3 z-10 flex flex-col items-end">
        <button
          onClick={() => { setShowLayers(v => !v); setShowStats(false); }}
          className={`rounded-full shadow p-2 ${visibleAreaIds.size > 0 || showPins || visibleCampusIds.size > 0 ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          title="Show saved maps"
        ><Layers className="w-5 h-5" /></button>
        {showLayers && (
          <div className="mt-2 w-56 bg-white rounded-lg shadow-lg border p-3 max-h-72 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-700 uppercase">Saved maps</span>
              <button onClick={() => setShowLayers(false)} className="text-gray-400"><X className="w-4 h-4" /></button>
            </div>
            {areas.length === 0 && pins.length === 0 && campuses.length === 0 ? (
              <p className="text-xs text-gray-400">No saved areas, pins, or schools for this project yet.</p>
            ) : (
              <>
                {areas.map(a => (
                  <div key={a.id} className="flex items-center gap-2 py-1 text-sm">
                    <input
                      type="checkbox"
                      checked={visibleAreaIds.has(a.id)}
                      onChange={(e) => setVisibleAreaIds(prev => {
                        const n = new Set(prev);
                        if (e.target.checked) n.add(a.id); else n.delete(a.id);
                        return n;
                      })}
                    />
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: a.color || '#4F46E5' }} />
                    <button type="button" onClick={() => focusArea(a)} className="truncate flex-1 text-left hover:text-indigo-600" title="Go to this area">{a.name}</button>
                  </div>
                ))}
                {areas.length > 0 && (
                  <button
                    onClick={() => setVisibleAreaIds(new Set(visibleAreaIds.size === areas.length ? [] : areas.map(a => a.id)))}
                    className="mt-1 text-xs text-indigo-600"
                  >{visibleAreaIds.size === areas.length ? 'Hide all areas' : 'Show all areas'}</button>
                )}
                {pins.length > 0 && (
                  <label className="flex items-center gap-2 py-1 mt-2 border-t pt-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={showPins} onChange={(e) => setShowPins(e.target.checked)} />
                    <span className="truncate">Show pins ({pins.length})</span>
                  </label>
                )}
                {campuses.length > 0 && (
                  <div className="mt-2 border-t pt-2">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Schools</div>
                    {campuses.map(c => (
                      <div key={c.name} className="flex items-center gap-2 py-0.5 text-sm">
                        <input
                          type="checkbox"
                          checked={visibleCampusIds.has(c.name)}
                          onChange={(e) => setVisibleCampusIds(prev => {
                            const n = new Set(prev);
                            if (e.target.checked) n.add(c.name); else n.delete(c.name);
                            return n;
                          })}
                        />
                        <span className="text-base flex-shrink-0">🏫</span>
                        <button
                          type="button"
                          onClick={() => {
                            setVisibleCampusIds(prev => new Set(prev).add(c.name));
                            if (mapRef) { mapRef.panTo({ lat: c.lat, lng: c.lng }); mapRef.setZoom(15); }
                            setShowLayers(false);
                          }}
                          className="truncate flex-1 text-left hover:text-indigo-600"
                          title="Go to this campus"
                        >{c.name}</button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Results — counts + toggle which outcome pins show on the map */}
      <div className="absolute top-28 right-3 z-10 flex flex-col items-end">
        <button
          onClick={() => { setShowStats(v => !v); setShowLayers(false); }}
          className={`rounded-full shadow p-2 ${visibleOutcomes.size < SEEK_OUTCOMES.length ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          title="Results &amp; pin filters"
        ><BarChart3 className="w-5 h-5" /></button>
        {showStats && (
          <div className="mt-2 w-56 bg-white rounded-lg shadow-lg border p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-700 uppercase">Seek &amp; Find results</span>
              <button onClick={() => setShowStats(false)} className="text-gray-400"><X className="w-4 h-4" /></button>
            </div>
            {counts.map(c => (
              <label key={c.value} className="flex items-center gap-2 py-1 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={visibleOutcomes.has(c.value)}
                  onChange={(e) => setVisibleOutcomes(prev => {
                    const n = new Set(prev);
                    if (e.target.checked) n.add(c.value); else n.delete(c.value);
                    return n;
                  })}
                />
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                <span className="flex-1">{c.label}</span>
                <span className="text-gray-500 font-medium">{c.n}</span>
              </label>
            ))}
            <p className="text-[11px] text-gray-400 mt-1.5 border-t pt-1.5">Uncheck to hide those pins from the map.</p>
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-white border-t p-3 space-y-2">
        <div className="flex items-center gap-2">
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional note for this spot…"
            className="flex-1 px-3 py-2 border rounded-lg text-sm"
          />
          {candidate && (
            <button onClick={() => setCandidate(null)} className="p-2 text-gray-400 hover:text-gray-600" title="Use my GPS instead"><X className="w-4 h-4" /></button>
          )}
        </div>
        <p className="text-xs text-gray-500 text-center">
          {candidate ? 'Using the tapped spot.' : activePoint ? 'Using your GPS location.' : 'Waiting for GPS…'} Tap the map to pick a different spot.
        </p>
        <div className="grid grid-cols-2 gap-2">
          {SEEK_OUTCOMES.map(o => (
            <button
              key={o.value}
              onClick={() => record(o.value)}
              disabled={saving || !activePoint}
              className="py-3 rounded-lg text-white font-semibold text-sm disabled:opacity-50 flex items-center justify-center"
              style={{ backgroundColor: o.color }}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : o.label}
            </button>
          ))}
        </div>
        {lastLogged && (
          <button
            type="button"
            onClick={undoLast}
            className="w-full text-xs text-gray-500 underline underline-offset-2 py-1"
          >
            Undo last entry ({seekOutcomeMeta(lastLogged.outcome).label})
          </button>
        )}
      </div>

      {/* Door survey modal (survey disposition) */}
      {surveyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <SurveyPreview
              survey={surveyModal.survey}
              mode="response"
              onSubmit={submitDoorSurvey}
              onLeadCaptureTriggered={surveyLeadCapture}
              onClose={() => setSurveyModal(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const SeekFindPage: React.FC = () => (
  <GoogleMapProvider>
    <SeekFindInner />
  </GoogleMapProvider>
);

export default SeekFindPage;
